import { formatDateTime } from "@/shared/datetime/formatters";

export const NS_PER_MS = 1e6;
export const NS_PER_MS_BIGINT = BigInt(NS_PER_MS);
export const MS_PER_S = 1000;
export const NS_PER_S = NS_PER_MS * MS_PER_S;
export const NS_PER_S_BIGINT = BigInt(NS_PER_S);

export const S_PER_MS = 1 / MS_PER_S;
export const S_PER_NS = 1 / NS_PER_S;
export const MS_PER_NS = 1 / NS_PER_MS;

export function milliSecToNanoSec(value: number): bigint {
  const ns = value * NS_PER_MS;
  return BigInt(ns.toFixed(0));
}

/**
 * Lossily converts a bigint nanosecond value into a floating-point milliseconds value.
 *
 * The conversion will remain accurate to ~microsecond precision.
 *
 * For more information, see {@link nanoSecToSecLossy}
 */
export function nanoSecToMilliSecLossy(value: bigint): number {
  const sec = Number(value / NS_PER_S_BIGINT);
  const nsec = Number(value % NS_PER_S_BIGINT);
  return sec * MS_PER_S + nsec / NS_PER_MS;
}

/**
 * Lossily converts a bigint nanosecond value into a floating-point seconds value.
 *
 * The conversion will remain accurate to ~microsecond precision.
 * The lossy conversion begins at the point where the bigint value passes
 * the limit of `Number.isSafeInteger`. You can experiment with that and `Date.now()*1000`
 * values to see this in action.
 *
 * For example:
 * `1629806438_269938658n` becomes
 * `1629806438.2699387`
 * ```
 *
 * For lossless conversion to a string-based fixed-point representation, see {@link nanoSecToSecStr}.
 */
export function nanoSecToSecLossy(value: bigint): number {
  const sec = Number(value / NS_PER_S_BIGINT);
  const nsec = Number(value % NS_PER_S_BIGINT);
  return sec + nsec / NS_PER_S;
}

/**
 * Digits kept after the decimal point in a fixed-point seconds rendering.
 * Nine is full nanosecond resolution; fewer keeps a 19-digit epoch value
 * readable in prose. The whole range is offered because the reader types the
 * count rather than picking from a menu.
 */
export type FractionDigits = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/**
 * Millisecond precision: the display default a reader gets before touching the
 * timestamp settings. `nanoSecToSecStr` itself defaults to full nanosecond
 * precision instead, so serialized values stay round-trippable.
 */
export const DEFAULT_FRACTION_DIGITS: FractionDigits = 3;

export function isFractionDigits(value: unknown): value is FractionDigits {
  return (
    Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 9
  );
}

/** Decimal digits a nanosecond value occupies below the second. */
const NANOSECOND_DIGITS: FractionDigits = 9;

/**
 * `10n ** BigInt(exponent)` for every exponent
 * {@link roundNsToFractionDigits} can need. Built once because that function
 * runs per rendered timestamp, and a chat transcript or a table page holds
 * hundreds of them.
 */
const POW10: readonly bigint[] = Array.from(
  { length: NANOSECOND_DIGITS + 1 },
  (_, exponent) => 10n ** BigInt(exponent),
);

/**
 * Rounds an epoch- or duration-nanosecond value to a whole multiple of the
 * smallest unit `fractionDigits` decimal places can express — half away from
 * zero, so `-1.5` units goes to `-2` and `1.5` to `2`.
 *
 * This is the one place any timestamp rendering drops precision. Every
 * precision-aware formatter rounds through it *first* and then formats the
 * rounded nanoseconds, so a fraction that carries into the next second is
 * carried by ordinary calendar arithmetic: `…23:59:59.9996` at three digits
 * becomes the next day's `00:00:00.000` because the rounded value genuinely is
 * that instant, not because a formatter patched up a `1.000` fraction after
 * the fact.
 *
 * Rounding runs entirely in bigint space: a 19-digit epoch value exceeds
 * `Number.MAX_SAFE_INTEGER`, so converting to a float first would corrupt the
 * low digits before there was anything to round. Doubling the discarded
 * remainder asks "at least half a unit?" without leaving bigint space. At full
 * nanosecond precision nothing is discarded, so the input is returned as-is.
 */
export function roundNsToFractionDigits(
  ns: bigint,
  fractionDigits: FractionDigits,
): bigint {
  if (fractionDigits === NANOSECOND_DIGITS) {
    return ns;
  }
  const nsPerUnit = POW10[NANOSECOND_DIGITS - fractionDigits];
  const negative = ns < 0n;
  const abs = negative ? -ns : ns;
  const remainder = abs % nsPerUnit;
  const truncated = abs - remainder;
  const rounded =
    remainder * 2n >= nsPerUnit ? truncated + nsPerUnit : truncated;
  return negative ? -rounded : rounded;
}

/**
 * Converts a bigint nanosecond value into a fixed-point seconds string, keeping
 * `fractionDigits` digits after the point and rounding half away from zero
 * (see {@link roundNsToFractionDigits}). Lossless at the default of nine
 * digits, which `strSecToBigIntNanosec` round-trips back to the original
 * bigint.
 */
export function nanoSecToSecStr(
  value: bigint,
  fractionDigits: FractionDigits = NANOSECOND_DIGITS,
): string {
  const rounded = roundNsToFractionDigits(value, fractionDigits);
  const negative = rounded < 0n;
  const abs = negative ? -rounded : rounded;

  // Whole units of the last digit kept. The rounding above already made `abs`
  // an exact multiple of one such unit, so this division is exact.
  const units = abs / POW10[NANOSECOND_DIGITS - fractionDigits];
  const unitsPerSec = POW10[fractionDigits];
  const seconds = units / unitsPerSec;
  const fraction = units % unitsPerSec;

  // A negative that rounds away to zero renders as "0", not "-0".
  const sign = negative ? "-" : "";
  if (fractionDigits === 0) {
    return `${sign}${seconds}`;
  }
  return `${sign}${seconds}.${fraction.toString().padStart(fractionDigits, "0")}`;
}

export function nanoSecToDate(ns: bigint) {
  // Date is built around millisecond precision.
  // For higher precision, we would need to look into an alternate date lib like Temporal.
  const ms = nanoSecToMilliSecLossy(ns);
  return new Date(ms);
}

/**
 * Fractional-second digits a local timestamp keeps unless asked otherwise:
 * milliseconds, which is both the finest a JS `Date` carries and the width
 * `seekParsers` round-trips.
 */
const LOCAL_TIMESTAMP_FRACTION_DIGITS: FractionDigits = 3;

/**
 * Renders an epoch-nanosecond instant as a local wall-clock timestamp,
 * keeping `fractionDigits` fractional-second digits (see
 * {@link dateToLocalTimestamp} for the shape). Sub-millisecond digits come
 * from the nanosecond value itself, so nine digits here shows nine real ones —
 * unlike {@link dateToLocalTimestamp}, whose input has nothing below the
 * millisecond.
 */
export function nanoSecToLocalTimestamp(
  ns: bigint,
  fractionDigits: FractionDigits = LOCAL_TIMESTAMP_FRACTION_DIGITS,
): string {
  const { date, fraction } = splitNanoSecForDisplay(ns, fractionDigits);
  return renderLocalTimestamp(date, fractionDigits, fraction);
}

/** An instant prepared for a date formatter that can only take a `Date`. */
export interface NanoSecDisplayParts {
  /** The rounded instant, carrying every field down to the millisecond. */
  readonly date: Date;
  /**
   * The fractional-second digits — exactly `fractionDigits` of them, `""` for
   * none. These, not the `Date`'s milliseconds, are what a formatter renders
   * after the decimal separator.
   */
  readonly fraction: string;
}

/**
 * Splits an epoch-nanosecond instant into the two things a `Date`-based
 * formatter needs to render it at nanosecond precision: a `Date` for the
 * calendar fields, and the fractional-second digits a `Date` cannot carry.
 *
 * Rounding to `fractionDigits` happens before the split, so a fraction that
 * carries is already in the `Date`'s seconds — `…59.9996` at three digits
 * hands back the next minute's `…00` with a `"000"` fraction, and no caller
 * has to notice.
 *
 * The sub-second remainder is normalized into `[0, 1s)`: bigint division
 * truncates toward zero, so a pre-epoch instant's raw remainder is negative
 * even though its wall clock reads a positive fraction of a second.
 */
export function splitNanoSecForDisplay(
  ns: bigint,
  fractionDigits: FractionDigits,
): NanoSecDisplayParts {
  const rounded = roundNsToFractionDigits(ns, fractionDigits);
  const date = nanoSecToDate(rounded);
  if (fractionDigits === 0) {
    return { date, fraction: "" };
  }
  const subSecondNs =
    ((rounded % NS_PER_S_BIGINT) + NS_PER_S_BIGINT) % NS_PER_S_BIGINT;
  const units = subSecondNs / POW10[NANOSECOND_DIGITS - fractionDigits];
  return { date, fraction: units.toString().padStart(fractionDigits, "0") };
}

/**
 * Cached `Intl.DateTimeFormat` per fractional-second width. Constructing one
 * dwarfs formatting with it, and these run once per rendered timestamp (a
 * plot's tick labels are dozens per frame) while only four widths ever occur.
 */
const localTimeFormatters = new Map<number, Intl.DateTimeFormat>();

function localTimeFormatter(
  fractionDigits: FractionDigits,
): Intl.DateTimeFormat {
  // Intl caps `fractionalSecondDigits` at 3. Requesting a width at all is only
  // how we get the locale's decimal separator and a `fractionalSecond` part to
  // substitute into; the digits Intl produces are never the ones rendered.
  const intlDigits = Math.min(fractionDigits, 3);
  let formatter = localTimeFormatters.get(intlDigits);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat(LOCAL_TIMESTAMP_LOCALE, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      ...(intlDigits === 0
        ? {}
        : { fractionalSecondDigits: intlDigits as 1 | 2 | 3 }),
      hour12: true,
      timeZoneName: "short",
    });
    localTimeFormatters.set(intlDigits, formatter);
  }
  return formatter;
}

/**
 * Assembles the local rendering from an already-rounded instant and the
 * fractional-second digits already computed for it. Substituting into the
 * `fractionalSecond` part (rather than concatenating) keeps whatever decimal
 * separator and field order the locale puts around it.
 */
function renderLocalTimestamp(
  date: Date,
  fractionDigits: FractionDigits,
  fraction: string,
): string {
  // Extract date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");

  // Format the date as YYYY-MM-DD
  const formattedDate = `${year}-${month}-${day}`;

  const formattedTime = localTimeFormatter(fractionDigits)
    .formatToParts(date)
    .map((part) => (part.type === "fractionalSecond" ? fraction : part.value))
    .join("")
    // Modern CLDR separates the AM/PM marker with a narrow no-break space
    // (U+202F). `Date.prototype.toLocaleTimeString`, which this used to call,
    // normalizes that to a plain space for web compatibility, and
    // `Intl.DateTimeFormat` does not — so normalizing here is what keeps the
    // shape `parseLocalDisplay` matches and a person can retype by hand.
    .replace(NARROW_SPACES, " ");

  return `${formattedDate} ${formattedTime}`;
}

/** The no-break spaces CLDR puts between time fields, none of them typeable. */
const NARROW_SPACES = /[\u202f\u00a0]/g;

/**
 * Format an epoch-nanosecond value for compact display, honoring the user's
 * active date/time format preference (e.g. "30.06.2026 14:05" under the Swiss
 * preset, "Jun 30, 2026, 2:05 PM" under the locale default). Returns `""` for
 * missing values or on any coercion error so a bad cell value can't break the
 * surrounding row. Prefer {@link nanoSecToLocalTimestamp} when precision
 * matters or when a stable, round-trippable serialization is required (the
 * visualization seek field relies on that fixed shape).
 */
export function formatNanoTimestamp(value: bigint | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  try {
    return formatDateTime(nanoSecToDate(BigInt(value)));
  } catch {
    return "";
  }
}

/**
 * Formats a nanosecond timestamp as a UTC ISO 8601 string, keeping
 * `fractionDigits` fractional-second digits (e.g.
 * "2024-03-15T17:40:45.123456789Z" at the nanosecond default,
 * "2024-03-15T17:40:45.123Z" at three, "2024-03-15T17:40:45Z" at zero — ISO
 * 8601 and RFC 3339 both allow any number of fractional digits, including
 * none, in which case the decimal separator goes too).
 *
 * `Date.toISOString` tops out at millisecond precision; this helper takes the
 * seconds-and-above portion from Date (which is lossless for any reasonable
 * timestamp's whole seconds) and appends the bigint nanosecond remainder as
 * the fraction. Rounding happens before the split, so a fraction that carries
 * lands in the seconds prefix rather than overflowing the digits after it.
 *
 * Assumes `ns >= 0n`; pre-epoch nanoseconds aren't a real input shape in
 * this app, and a negative remainder would render as "-NNN" inside the
 * fraction. The guard collapses sub-zero inputs to an all-zero fraction.
 */
export function nanoSecToUTCTimestamp(
  ns: bigint,
  fractionDigits: FractionDigits = NANOSECOND_DIGITS,
): string {
  const rounded = ns >= 0n ? roundNsToFractionDigits(ns, fractionDigits) : ns;
  // Strip the sub-second remainder before the Date conversion so the input to
  // `nanoSecToMilliSecLossy` is an exact integer-second value. For ns in the
  // final ~122 ns of a second, the float path otherwise rounds `sec * 1000 +
  // 999.999999...` up to `(sec + 1) * 1000`, producing an ISO prefix one
  // second ahead of the appended fractional digits.
  const fractionalNs = rounded >= 0n ? rounded % NS_PER_S_BIGINT : 0n;
  const isoSecondsPrefix = nanoSecToDate(rounded - fractionalNs)
    .toISOString()
    .slice(0, 19);
  if (fractionDigits === 0) {
    return `${isoSecondsPrefix}Z`;
  }
  const fraction = fractionalNs / POW10[NANOSECOND_DIGITS - fractionDigits];
  return `${isoSecondsPrefix}.${fraction.toString().padStart(fractionDigits, "0")}Z`;
}

// Centralized Unix-epoch-nanosecond conversions for `number`-typed ns values.
// Aggregate buckets and query bounds are carried as ns ints throughout the
// metrics domain; these helpers are the single place we drop to JS `Date`
// (millisecond) precision. Precision below 1ms is irrelevant for the
// day/week/month bucket edges we display.

/** Nanoseconds → milliseconds. */
export function nsToMs(ns: number): number {
  return ns / NS_PER_MS;
}

/** Nanoseconds → ISO-8601 UTC string. Bridges aggregate ns to the ISO-string
 *  contract of `getDrillDownTitle`. */
export function nsToIso(ns: number): string {
  return new Date(nsToMs(ns)).toISOString();
}

/** Nanoseconds → short ISO date (`YYYY-MM-DD`), used for chart x-axis labels. */
export function nsToDateLabel(ns: number): string {
  return nsToIso(ns).slice(0, 10);
}

// Pinned locale for the local-time component of `dateToLocalTimestamp`. The
// output shape ("HH:MM:SS.mmm AM/PM TZ", with two-digit hours) is load-bearing
// for downstream consumers — `DateTimeSelector` renders a 12-hour picker on
// the same en-US assumption, and `seekParsers` parses the shape back. Letting
// the user's locale drive the format would break those consumers (24-hour
// locales would produce no AM/PM marker, different separators would shift the
// field boundaries).
const LOCAL_TIMESTAMP_LOCALE = "en-US";

/**
 * Renders a `Date` as "YYYY-MM-DD HH:MM:SS.mmm AM/PM TZ" in the viewer's zone,
 * keeping `fractionDigits` fractional-second digits (three by default; zero
 * drops the decimal separator too).
 *
 * A `Date` holds nothing below the millisecond, so digits past the third are
 * always zeros here — {@link nanoSecToLocalTimestamp} is the entry point that
 * can fill them.
 */
export function dateToLocalTimestamp(
  date: Date,
  fractionDigits: FractionDigits = LOCAL_TIMESTAMP_FRACTION_DIGITS,
): string {
  const time = date.getTime();
  if (Number.isNaN(time)) {
    // `Intl` throws on an invalid date where the old `toLocaleTimeString` path
    // returned "Invalid Date". Chart tick callbacks and table cells call this;
    // a thrown RangeError there would take down the surrounding render.
    return "Invalid Date";
  }
  // Round in nanosecond space so a fraction narrower than the Date's own
  // milliseconds carries into the seconds (`…08.999` at two digits is
  // `…09.00`, not `…08.100`).
  return nanoSecToLocalTimestamp(
    BigInt(time) * NS_PER_MS_BIGINT,
    fractionDigits,
  );
}

/**
 * Converts a string representing a number of seconds to a BigInt in nanoseconds,
 * rounding fractional digits finer than a nanosecond to the nearest nanosecond.
 *
 * @param value - The string representing seconds, possibly with fractional seconds.
 * @returns The equivalent BigInt in nanoseconds.
 */
export function strSecToBigIntNanosec(value: string): bigint {
  return strTimeToBigIntNanosec(value, "s");
}

/**
 * Converts a decimal string counting `unit`s of time to a BigInt in nanoseconds,
 * rounding fractional digits finer than a nanosecond to the nearest nanosecond.
 *
 * @param value - The decimal string, possibly signed and with a fractional part.
 * @param unit - The unit `value` counts.
 * @returns The equivalent BigInt in nanoseconds.
 */
export function strTimeToBigIntNanosec(value: string, unit: TimeUnit): bigint {
  const [integerPart, decimalPart = "0"] = value.split(".");

  const sign = Number(value) < 0 ? -1n : 1n;
  // Parse the integer part of the number as a positive number.
  let intPart = BigInt(integerPart) * sign;
  const nsPerUnit = unitToNanoSecBigIntScale[unit];

  const digitsShort = unitToNanoSecExponent[unit] - decimalPart.length;
  let nsec = BigInt(
    // Round because we could get more digits than nanosecond precision.
    Math.round(parseInt(decimalPart, 10) * Math.pow(10, digitsShort)),
  );

  // Rounding can carry the fraction up to one whole unit ("0.9999999999" s
  // rounds to 1e9 ns); move that carry into the integer part.
  if (nsec >= nsPerUnit) {
    intPart += nsec / nsPerUnit;
    nsec = nsec % nsPerUnit;
  }

  return sign * (intPart * nsPerUnit + nsec);
}

export function numberTimeToBigIntNanosec(value: number, unit: TimeUnit) {
  return BigInt(Math.round(value * unitToNanoSecNumberScale[unit]));
}

export function bigIntTimeToBigIntNanosec(value: bigint, unit: TimeUnit) {
  return value * unitToNanoSecBigIntScale[unit];
}

/** The power of 10 that relates each TimeUnit to nanoseconds. */
const unitToNanoSecExponent: Record<TimeUnit, number> = {
  s: 9,
  ms: 6,
  us: 3,
  ns: 0,
};

/** Nanoseconds per unit, as a number. */
const unitToNanoSecNumberScale: Record<TimeUnit, number> = {
  s: NS_PER_S,
  ms: NS_PER_MS,
  us: 1e3,
  ns: 1,
};

/** Nanoseconds per unit, as a bigint. */
const unitToNanoSecBigIntScale: Record<TimeUnit, bigint> = {
  s: NS_PER_S_BIGINT,
  ms: NS_PER_MS_BIGINT,
  us: 1_000n,
  ns: 1n,
};

export type TimeUnit = "s" | "ms" | "us" | "ns";

/**
 * Time input the metrics SDK accepts.
 *
 * `number` is MILLISECONDS — the JavaScript convention (`Date.now()`,
 * `Date.prototype.getTime`). `string` is ISO 8601. `bigint` is epoch
 * NANOSECONDS, for callers who genuinely need sub-millisecond precision.
 *
 * This deliberately diverges from the Python SDK, where a bare `int` means
 * nanoseconds: Python's `int` is arbitrary-precision so ns is lossless there,
 * whereas a JS `number` cannot represent epoch-ns exactly. Defaulting to ns
 * here would not be merely different — it would be wrong.
 */
export type Time = Date | number | string | bigint;

/**
 * Coerce {@link Time} to epoch nanoseconds, exactly.
 *
 * The multiplication happens in bigint space. Epoch-ns exceeds
 * `Number.MAX_SAFE_INTEGER` by ~198x, so `ms * 1_000_000` rounds to the
 * nearest representable double — wrong for roughly three of every four
 * millisecond inputs. (`milliSecToNanoSec` above has that flaw: it multiplies
 * as a double before widening.)
 */
export function toEpochNanoseconds(value: Time): bigint {
  if (typeof value === "bigint") {
    return value;
  }
  const ms = toEpochMilliseconds(value);
  if (!Number.isFinite(ms)) {
    throw new Error(
      `Could not read a time from ${JSON.stringify(String(value))}`,
    );
  }
  return BigInt(Math.round(ms)) * NS_PER_MS_BIGINT;
}

/** The millisecond value behind a non-bigint {@link Time}. `NaN` for a string
 *  that is not a readable date — the caller turns that into an error. */
function toEpochMilliseconds(value: Date | number | string): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  return Date.parse(value);
}
